import React, { useEffect, useState, forwardRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTheme, useMediaQuery } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import MaterialTable from '../components/MaterialTableAdapter'
import { toast } from 'react-toastify'
import {
  FileDown,
  FileSpreadsheet,
  LogOut,
  Trash2,
  UserRoundPlus,
  UserRoundX,
} from 'lucide-react'

import Input from '../components/Input'
import Loading from '../components/Loading'
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import Select from '../components/Select'
import { exportToExcel, exportToPDF } from '../utils/exportUtils'

import { addUser, getUsers, forceLogoutUser, forceLogoutMultipleUsers, deleteAdmin, bulkDeleteAdmins } from '../actions/AdminAction'
import { listClubs } from '../actions/clubActions'
import { getRoles } from '../actions/roleActions'
import {
  ADD_USER_RESET,
  GET_ADMINS_RESET,
  FORCE_LOGOUT_USER_RESET,
  FORCE_LOGOUT_MULTIPLE_RESET,
  DELETE_ADMIN_RESET,
  BULK_DELETE_ADMINS_RESET,
} from '../constants/adminConstant'
import ScreenHeader from '../components/layout/ScreenHeader'
import { hasPermission } from '../constants/rolePermissions'


const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

// An admin is "Online" while they have a live login session. Older API responses
// without session counts fall back to: active within the last 30 minutes AND not
// force-logged-out since.
const isAdminOnline = (user) => {
  if (typeof user?.activeSessionCount === 'number') return user.activeSessionCount > 0
  if (!user?.lastLogin) return false

  const lastLoginTime = new Date(user.lastLogin)
  const diffMinutes = (Date.now() - lastLoginTime.getTime()) / (1000 * 60)
  if (diffMinutes >= 30) return false

  if (user.lastForceLogout && new Date(user.lastForceLogout) > lastLoginTime) {
    return false
  }

  return true
}

const UserList = ({ history }) => {
  // * States
  const navigate = useNavigate();
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([]); // State for filtered data
  const [rolesFromServer, setRolesFromServer] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  // * Clubs for Admin Users
  const [clubOptions, setClubOptions] = useState([])
  const [selectedClubs, setSelectedClubs] = useState([])
  // * Add a User
  const [openModal, setOpenModal] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [createdAdminCredentials, setCreatedAdminCredentials] = useState(null)
  const [selectAllClubs, setSelectAllClubs] = useState(false);
  const [clubDropdownOpen, setClubDropdownOpen] = useState(false);
  const [clubSearchTerm, setClubSearchTerm] = useState('');

  // * Force Logout States
  const [forceLogoutModal, setForceLogoutModal] = useState(false)
  const [selectedUserForLogout, setSelectedUserForLogout] = useState(null)
  const [selectedUsersForBulkLogout, setSelectedUsersForBulkLogout] = useState([])
  const [bulkForceLogoutModal, setBulkForceLogoutModal] = useState(false)

  // * Delete modals
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null)
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false)

  // * Roles
  const [addUserOption, setAddUserOption] = useState(false)
  const [forceLogoutOption, setForceLogoutOption] = useState(false)
  const [deleteOption, setDeleteOption] = useState(false)
  const [loading, setLoading] = useState(true)


  // * Initialization
  const dispatch = useDispatch()

  // * Check for auth
  const userLogin = useSelector((state) => state.adminLogin)
  const { loadingUserInfo, userInfo } = userLogin

  useEffect(() => {
    // * Check if user info exists
    if (!userInfo) {
      // history.push('/')
    }
  }, [userInfo, history])

  // * Check for role
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { loadingGetRole, getRoleData } = getRoleInfo

  useEffect(() => {
    // Check getRoleData first, then fallback to userInfo.role
    const roleData = getRoleData || userInfo?.role

    if (roleData) {
      console.log('Role permissions:', roleData)
      setAddUserOption(hasPermission(roleData, 'usersAdd'))
      setForceLogoutOption(hasPermission(roleData, 'usersUpdate'))
      setDeleteOption(hasPermission(roleData, 'usersDelete'))
      if (!hasPermission(roleData, 'usersView')) {
        toast('You do not have permission to view this page', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/dashboard')
      }
    }
  }, [userInfo, getRoleData, navigate])

  useEffect(() => {
    if (selectAllClubs) {
      setSelectedClubs(clubOptions.map(item => item.id));
    } else {
      setSelectedClubs([]);
    }
  }, [selectAllClubs, clubOptions]);

  const selectedRoleDetails = rolesFromServer.find((item) => item._id === role)
  const selectedRoleIsSuperAdmin = selectedRoleDetails?.isSuperAdmin === true
  const showClubAssignment = Boolean(role) && !selectedRoleIsSuperAdmin

  useEffect(() => {
    if (selectedRoleIsSuperAdmin) {
      setSelectedClubs([])
      setSelectAllClubs(false)
      setClubDropdownOpen(false)
      setClubSearchTerm('')
    }
  }, [selectedRoleIsSuperAdmin])

  const handleClubChange = (id) => {
    if (selectedClubs.includes(id)) {
      setSelectedClubs(selectedClubs.filter(clubId => clubId !== id));
    } else {
      setSelectedClubs([...selectedClubs, id]);
    }
  };

  const handleSelectAllClubsChange = () => {
    setSelectAllClubs(!selectAllClubs);
  };

  // Debug loop
  const hasFetched = React.useRef(false)

  useEffect(() => {
    console.log('UserList MOUNTED')
    return () => console.log('UserList UNMOUNTED')
  }, [])

  useEffect(() => {
    if (userInfo && !hasFetched.current) {
      console.log('UserList: Fetching initial data')
      dispatch(getUsers()) // Admin users
      // Fetch clubs for admin assignment
      if (userInfo?.role?.clubsView || userInfo?.role?.adminClubAssignment) {
        dispatch(listClubs())
      }
      dispatch(getRoles())
      hasFetched.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userInfo?.id])

  // * Get Admin Users (from AdminAction)
  const getAdminUsersInfo = useSelector((state) => state.getAdminsInfo)
  const { loadingGetUsersInfo, errorGetUsersInfo, getUsersData } = getAdminUsersInfo

  // Handle admin users data
  useEffect(() => {
    if (getUsersData) {
      setLoading(false)
      console.log('Admin Users Data:', getUsersData)

      // Debug: Log first user's fields
      const usersData = getUsersData.users || getUsersData
      if (Array.isArray(usersData) && usersData.length > 0) {
        console.log('First admin user details:', JSON.stringify(usersData[0], null, 2))
        console.log('First admin fields - name:', usersData[0].name, 'email:', usersData[0].email, 'role:', usersData[0].role)
      }

      // The /admins API already returns only admin users, no need to filter by role
      if (Array.isArray(usersData)) {
        // All users from /admins endpoint are admin users
        setData(usersData)
        setFilteredData(usersData)
      }
    } else if (errorGetUsersInfo) {
      setLoading(false)
      toast(errorGetUsersInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getUsersData, errorGetUsersInfo])

  // Get clubs data
  const clubList = useSelector((state) => state.clubList)
  const { clubs = [] } = clubList || {}

  useEffect(() => {
    if (clubs && Array.isArray(clubs) && clubs.length > 0) {
      const data = clubs.map((item) => ({
        id: item._id,
        title: item.name,
      }))
      setClubOptions(data)
    }
  }, [clubs])

  // Get roles data
  const getRolesInfo = useSelector((state) => state.getRolesInfo)
  const { getRolesData } = getRolesInfo

  useEffect(() => {
    if (getRolesData && Array.isArray(getRolesData)) {
      setRolesFromServer(getRolesData)
    }
  }, [getRolesData])

  useEffect(() => {
    if (Array.isArray(rolesFromServer) && rolesFromServer.length > 0) {
      const data = rolesFromServer.map((item) => ({
        id: item._id,
        title: item.name,
      }))
      setRoleOptions(data)
    }
  }, [rolesFromServer])

  // * Force Logout Single User
  const openForceLogoutModal = (user) => {
    setSelectedUserForLogout(user)
    setForceLogoutModal(true)
  }

  const closeForceLogoutModal = () => {
    setSelectedUserForLogout(null)
    setForceLogoutModal(false)
  }

  const handleForceLogout = () => {
    if (selectedUserForLogout) {
      dispatch(forceLogoutUser(selectedUserForLogout._id))
    }
  }

  // * Force Logout Multiple Users
  const openBulkForceLogoutModal = () => {
    if (selectedUsersForBulkLogout.length === 0) {
      toast('Please select users to force logout', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    setBulkForceLogoutModal(true)
  }

  const closeBulkForceLogoutModal = () => {
    setBulkForceLogoutModal(false)
  }

  const handleBulkForceLogout = () => {
    const userIds = selectedUsersForBulkLogout.map(user => user._id)
    dispatch(forceLogoutMultipleUsers(userIds))
  }

  // * Delete Single Admin
  const openDeleteModal = (user) => {
    setSelectedUserForDelete(user)
    setDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setSelectedUserForDelete(null)
    setDeleteModal(false)
  }

  const handleDeleteAdmin = () => {
    if (selectedUserForDelete) {
      dispatch(deleteAdmin(selectedUserForDelete._id)).catch(() => { })
    }
  }

  // * Delete Multiple Admins
  const openBulkDeleteModal = () => {
    if (selectedUsersForBulkLogout.length === 0) {
      toast('Please select users to delete', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    setBulkDeleteModal(true)
  }

  const closeBulkDeleteModal = () => {
    setBulkDeleteModal(false)
  }

  const handleBulkDeleteAdmins = () => {
    const userIds = selectedUsersForBulkLogout.map(user => user._id)
    dispatch(bulkDeleteAdmins(userIds)).catch(() => { })
  }

  // * Export Handlers
  const prepareExportData = (dataToExport) => {
    return dataToExport.map(user => {
      let displayName = user.name || user.email || 'N/A';

      // Status logic
      const currentUserId = JSON.parse(localStorage.getItem('adminInfo'))?.id;
      const isCurrentUser = user._id === currentUserId;
      const online = isCurrentUser || isAdminOnline(user);

      return {
        Name: displayName,
        Email: user.email || 'N/A',
        Role: user.role?.name || 'N/A',
        'Join Date': user.joinedOn ? new Date(user.joinedOn).toLocaleDateString() : 'N/A',
        Status: online ? 'Online' : 'Offline',
      };
    });
  };

  const handleExportExcel = () => {
    const exportData = prepareExportData(filteredData);
    const columns = [
      { title: 'Name', field: 'Name' },
      { title: 'Email', field: 'Email' },
      { title: 'Role', field: 'Role' },
      { title: 'Join Date', field: 'Join Date' },
      { title: 'Status', field: 'Status' },
    ];
    exportToExcel(exportData, columns, 'AdminUsers');
  };

  const handleExportPDF = () => {
    const exportData = prepareExportData(filteredData);
    const columns = [
      { title: 'Name', field: 'Name' },
      { title: 'Email', field: 'Email' },
      { title: 'Role', field: 'Role' },
      { title: 'Join Date', field: 'Join Date' },
      { title: 'Status', field: 'Status' },
    ];
    exportToPDF(exportData, columns, 'AdminUsers', 'Admin Users List');
  };

  // * Users Table
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getHeadCells = () => {
    const baseColumns = [
      {
        field: 'srNo',
        title: '#',
        sorting: false,
        render: (rowData) => {
          const idx = data.findIndex((d) => d._id === rowData._id)
          return <span className='text-gray-500'>{idx + 1}</span>
        },
        cellStyle: { padding: isMobile ? '8px 4px' : '16px', width: 48 }
      },
      {
        field: 'name',
        title: 'Name',
        render: (rowData) => {
          return (
            <Link
              className='inline-flex font-semibold text-cm-text hover:text-cm-gold-text underline-offset-4 hover:underline truncate max-w-[160px] sm:max-w-none'
              to={`/users/${rowData._id}`}
              state={{ isAdmin: true }}
            >
              {rowData.name || rowData.email || 'N/A'}
            </Link>
          );
        },
        cellStyle: { padding: isMobile ? '8px 4px' : '16px' }
      },
      // Hide Email on Mobile
      ...(!isMobile ? [{
        field: 'email',
        title: 'Email',
        render: (rowData) => rowData.email || 'N/A',
      }] : [])
    ]

    // Add Role column - Hide on mobile
    if (!isMobile) {
      baseColumns.push({
        field: 'role',
        title: 'Role',
        render: (rowData) => rowData.role?.name || 'N/A',
      })
    }

    // Joined On - Hide on mobile
    if (!isTablet) {
      baseColumns.push({
        field: 'joinedOn',
        title: 'Joined',
        render: (rowData) => {
          if (rowData.joinedOn) {
            try {
              return new Date(rowData.joinedOn).toLocaleDateString()
            } catch (error) {
              return rowData.joinedOn
            }
          }
          return 'N/A'
        },
      });
    }

    // Status Column - Compact on mobile
    baseColumns.push({
      field: 'calling',
      title: 'Status',
      render: (rowData) => {
        // For the currently logged-in user, always show Online
        const currentUserId = JSON.parse(localStorage.getItem('adminInfo'))?.id;
        const isCurrentUser = rowData._id === currentUserId;

        // For other users, check lastLogin within last 30 minutes and that
        // the session hasn't been force-logged-out since.
        const online = isCurrentUser || isAdminOnline(rowData);

        return (
          <span className={`px-2 py-1 rounded text-[10px] sm:text-xs font-medium ${online
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
            }`}>
            {online ? 'Online' : 'Offline'}
          </span>
        );
      },
      cellStyle: { padding: isMobile ? '8px 4px' : '16px' }
    });

    // Actions Column - Icon only on mobile
    baseColumns.push({
      title: 'Action',
      sorting: false,
      headerClassName: 'action-column-header',
      bodyClassName: 'action-column-cell',
      headerStyle: { textAlign: 'center', width: 180, minWidth: 180 },
      render: (rowData) => {
        if (!rowData || !rowData._id) return null;

        return (
          <div className='flex w-full items-center justify-center gap-2'>
            {forceLogoutOption && userInfo?.id !== rowData._id && (
              <button
                onClick={() => openForceLogoutModal(rowData)}
                className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 transition-all hover:-translate-y-0.5 hover:bg-amber-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400'
                title='Force Logout'
                aria-label='Force Logout'
              >
                <UserRoundX size={16} strokeWidth={2} />
              </button>
            )}
            {deleteOption && userInfo?.id !== rowData._id && (
              <button
                onClick={() => openDeleteModal(rowData)}
                className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 transition-all hover:-translate-y-0.5 hover:bg-rose-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-400'
                title='Delete User'
                aria-label='Delete User'
              >
                <Trash2 size={16} strokeWidth={2} />
              </button>
            )}
          </div>
        );
      },
      cellStyle: {
        padding: isMobile ? '8px 4px' : '16px',
        textAlign: 'center',
        width: 180,
        minWidth: 180,
      }
    });

    return baseColumns
  }

  // * Open Add User Modal
  const openAddUserModal = () => {
    setOpenModal(true)
  }

  // * Close Add User Modal
  const closeAddUserModal = () => {
    setOpenModal(false)
    setEmail('')
    setName('')
    setRole('')
    setSelectedClubs([])
    setSelectAllClubs(false)
    setClubDropdownOpen(false)
    setClubSearchTerm('')
  }

  // * Add User
  const addUserHandler = () => {
    const normalizedName = name.trim()
    const normalizedEmail = email.trim().toLowerCase()
    const emailRegex = /^[^\s@]+@[^\s@]+$/

    if (!normalizedName || !normalizedEmail || !role) {
      toast('All the fields are mandatory', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    if (!emailRegex.test(normalizedEmail)) {
      toast('Please enter a valid email address', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    if (!selectedRoleIsSuperAdmin && selectedClubs.length === 0) {
      toast('Please select at least one club', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }

    dispatch(
      addUser(
        normalizedName,
        normalizedEmail,
        role,
        selectedRoleIsSuperAdmin ? [] : selectedClubs,
      ),
    )
  }

  const addUserInfo = useSelector((state) => state.addAdminInfo)
  const { loadingAddUserInfo, errorAddUserInfo, addUserData } = addUserInfo

  useEffect(() => {
    dispatch({ type: ADD_USER_RESET })
    if (addUserData) {
      const generatedPassword =
        typeof addUserData === 'string' ? addUserData : addUserData?.password
      const createdEmail = email.trim().toLowerCase()

      toast('User added successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 2000,
      })

      if (generatedPassword) {
        setCreatedAdminCredentials({
          email: createdEmail,
          password: generatedPassword,
        })
      }

      closeAddUserModal()
      // Refresh user list after successful add
      setTimeout(() => {
        dispatch(getUsers())
      }, 1000)
    } else if (errorAddUserInfo) {
      toast(errorAddUserInfo, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [addUserData, errorAddUserInfo, dispatch, email])

  // * Force Logout Handlers
  const forceLogoutUserInfo = useSelector((state) => state.forceLogoutUserInfo)
  const { loadingForceLogoutUser, errorForceLogoutUser, forceLogoutUserData } = forceLogoutUserInfo

  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_USER_RESET })
    if (forceLogoutUserData) {
      toast(forceLogoutUserData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      closeForceLogoutModal()
      setTimeout(() => {
        dispatch(getUsers())
      }, 1000)
    } else if (errorForceLogoutUser) {
      toast(errorForceLogoutUser, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [forceLogoutUserData, errorForceLogoutUser, dispatch])

  const forceLogoutMultipleInfo = useSelector((state) => state.forceLogoutMultipleInfo)
  const { loadingForceLogoutMultiple, errorForceLogoutMultiple, forceLogoutMultipleData } = forceLogoutMultipleInfo

  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_MULTIPLE_RESET })
    if (forceLogoutMultipleData) {
      toast(forceLogoutMultipleData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      setSelectedUsersForBulkLogout([])
      closeBulkForceLogoutModal()
      setTimeout(() => {
        dispatch(getUsers())
      }, 1000)
    } else if (errorForceLogoutMultiple) {
      toast(errorForceLogoutMultiple, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [forceLogoutMultipleData, errorForceLogoutMultiple, dispatch])

  // * Delete Handlers
  const deleteAdminInfo = useSelector((state) => state.deleteAdminInfo)
  const { loadingDeleteAdmin, errorDeleteAdmin, successDeleteAdmin } = deleteAdminInfo

  useEffect(() => {
    if (successDeleteAdmin) {
      dispatch({ type: DELETE_ADMIN_RESET })
      toast('User deleted successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      closeDeleteModal()
      dispatch(getUsers())
    } else if (errorDeleteAdmin) {
      dispatch({ type: DELETE_ADMIN_RESET })
      toast(errorDeleteAdmin, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 3000,
      })
    }
  }, [successDeleteAdmin, errorDeleteAdmin, dispatch])

  const bulkDeleteAdminsInfo = useSelector((state) => state.bulkDeleteAdminsInfo)
  const {
    loadingBulkDeleteAdmins,
    errorBulkDeleteAdmins,
    successBulkDeleteAdmins,
    bulkDeleteAdminsData,
  } = bulkDeleteAdminsInfo

  useEffect(() => {
    if (successBulkDeleteAdmins) {
      dispatch({ type: BULK_DELETE_ADMINS_RESET })
      toast(`${bulkDeleteAdminsData?.deletedCount || 0} user(s) deleted successfully`, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      const skipped = bulkDeleteAdminsData?.skipped || []
      if (skipped.length > 0) {
        toast(`${skipped.length} skipped: ${skipped[0].reason}`, {
          type: 'warning',
          hideProgressBar: true,
          autoClose: 4000,
        })
      }
      setSelectedUsersForBulkLogout([])
      closeBulkDeleteModal()
      dispatch(getUsers())
    } else if (errorBulkDeleteAdmins) {
      dispatch({ type: BULK_DELETE_ADMINS_RESET })
      toast(errorBulkDeleteAdmins, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 3000,
      })
    }
  }, [successBulkDeleteAdmins, errorBulkDeleteAdmins, bulkDeleteAdminsData, dispatch])

  if (loading) {
    return <Loading />
  }

  return (
    <div className='w-full h-full'>
      <ScreenHeader title="Admin users" />

      <MaterialTable
        toolbarActions={
          <>
            {forceLogoutOption && (
              <button
                type='button'
                onClick={openBulkForceLogoutModal}
                title='Force Logout Selected'
                aria-label='Force Logout Selected'
                className='inline-flex h-10 w-10 items-center justify-center rounded-md bg-orange-100 text-orange-600 hover:bg-orange-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-400'
              >
                <LogOut size={18} strokeWidth={2.2} />
              </button>
            )}
            {deleteOption && (
              <button
                type='button'
                onClick={openBulkDeleteModal}
                title='Delete Selected'
                aria-label='Delete Selected'
                className='inline-flex h-10 w-10 items-center justify-center rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400'
              >
                <Trash2 size={18} strokeWidth={2.2} />
              </button>
            )}
            {addUserOption && (
              <button
                type='button'
                onClick={openAddUserModal}
                title='Add Admin User'
                aria-label='Add Admin User'
                className='inline-flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-400'
              >
                <UserRoundPlus size={18} strokeWidth={2.2} />
              </button>
            )}
            <button type='button' onClick={handleExportExcel} title='Export to Excel' aria-label='Export to Excel' className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200 transition-all hover:bg-teal-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-cm-gold'>
              <FileSpreadsheet size={18} strokeWidth={2} />
            </button>
            <button type='button' onClick={handleExportPDF} title='Export to PDF' aria-label='Export to PDF' className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 transition-all hover:bg-violet-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400'>
              <FileDown size={18} strokeWidth={2} />
            </button>
          </>
        }
        icons={tableIcons}
        columns={getHeadCells()}
        data={data}
        options={{
          exportButton: false,
          search: true,
          exportAllData: false,
          rowStyle: {
            height: '5px',
            fontSize: 13,
          },
          paging: true,
          pageSize: 10,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [10, 20, 50],
          headerStyle: {
            position: 'sticky',
            top: '0',
          },
          selection: forceLogoutOption || deleteOption,
          selectionProps: rowData => ({
            disabled: userInfo?.id === rowData._id,
          }),
          onValueChange: (value) => setFilteredData(value),
        }}
        onSearchChange={(searchText) => {
          if (searchText) {
            dispatch(getUsers(searchText))
          } else {
            dispatch(getUsers())
          }
        }}
        onSelectionChange={(rows) => setSelectedUsersForBulkLogout(rows)}
      />

      {/* Add User Modal - Only for admin users */}
      {openModal && (
        <>
          {/* Modal Overlay */}
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>

          {/* Modal Container */}
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto min-h-screen">
            <div className="relative w-full max-w-xl mx-auto bg-white rounded-lg shadow-lg">

              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Add Admin User</h3>
              </div>

              {/* Modal Body */}
              <div className="px-4 py-4">
                <Input
                  width="w-full mb-4"
                  name="Name *"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                {/* Email */}
                <Input
                  width="w-full mb-4"
                  name="Email *"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                {/* Role */}
                <Select
                  width="w-full mb-4"
                  name="Role *"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  options={roleOptions}
                />

                {selectedRoleIsSuperAdmin && (
                  <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                    Super Admin has access across all clubs. Club assignment is optional.
                  </div>
                )}

                {/* Club Assignment - Required for non-super-admin roles */}
                {showClubAssignment && (
                  <>
                    <p className="text-sm mb-2 font-medium">Assign Clubs *</p>

                    {/* Dropdown Container */}
                    <div className="relative">
                      {/* Dropdown Trigger */}
                      <div
                        className="w-full border border-gray-300 rounded-lg p-3 cursor-pointer bg-white hover:border-emerald-500 transition-colors flex justify-between items-center"
                        onClick={() => setClubDropdownOpen(!clubDropdownOpen)}
                      >
                        <div className="flex-1">
                          {selectedClubs.length === 0 ? (
                            <span className="text-gray-400">Select clubs...</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {selectedClubs.slice(0, 3).map((clubId) => {
                                const club = clubOptions.find(c => c.id === clubId);
                                return club ? (
                                  <span
                                    key={clubId}
                                    className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-medium"
                                  >
                                    {club.title}
                                  </span>
                                ) : null;
                              })}
                              {selectedClubs.length > 3 && (
                                <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                  +{selectedClubs.length - 3} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${clubDropdownOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Dropdown Panel */}
                      {clubDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-hidden">
                          {/* Search Input */}
                          <div className="p-2 border-b border-gray-200">
                            <input
                              type="text"
                              placeholder="Search clubs..."
                              value={clubSearchTerm}
                              onChange={(e) => setClubSearchTerm(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Select All Option */}
                          <div
                            className="flex items-center px-3 py-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectAllClubsChange();
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectAllClubs}
                              onChange={() => { }}
                              className="mr-3 h-4 w-4 accent-emerald-500 cursor-pointer"
                            />
                            <span className="text-sm font-semibold text-gray-700">Select All Clubs</span>
                          </div>

                          {/* Club List */}
                          <div className="max-h-44 overflow-y-auto">
                            {clubOptions.length > 0 ? (
                              clubOptions
                                .filter(club =>
                                  club.title.toLowerCase().includes(clubSearchTerm.toLowerCase())
                                )
                                .map((item) => (
                                  <div
                                    key={item.id}
                                    className={`flex items-center px-3 py-2 hover:bg-emerald-50 cursor-pointer transition-colors ${selectedClubs.includes(item.id) ? 'bg-emerald-50' : ''
                                      }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClubChange(item.id);
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedClubs.includes(item.id)}
                                      onChange={() => { }}
                                      className="mr-3 h-4 w-4 accent-emerald-500 cursor-pointer"
                                    />
                                    <span className="text-sm text-gray-700">{item.title}</span>
                                  </div>
                                ))
                            ) : (
                              <p className="text-sm text-gray-500 italic p-3">No clubs available. Create clubs first.</p>
                            )}
                            {clubOptions.length > 0 &&
                              clubOptions.filter(club =>
                                club.title.toLowerCase().includes(clubSearchTerm.toLowerCase())
                              ).length === 0 && (
                                <p className="text-sm text-gray-500 italic p-3">No clubs found matching "{clubSearchTerm}"</p>
                              )
                            }
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Selected Count */}
                    {selectedClubs.length > 0 && (
                      <p className="text-xs text-emerald-600 mt-2 font-medium">
                        {selectedClubs.length} club(s) selected
                      </p>
                    )}
                  </>
                )}

                {/* Note */}
                <p className="text-xs text-gray-500 mt-4">
                  All the fields with * are mandatory
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end px-4 py-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeAddUserModal}
                  className="text-red-500 font-bold uppercase text-sm px-4 py-2 mr-2"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={addUserHandler}
                  disabled={loadingAddUserInfo}
                  className={`${loadingAddUserInfo ? 'bg-gray-300' : 'bg-emerald-500 hover:bg-emerald-600'
                    } text-white font-bold uppercase text-sm px-6 py-2 rounded shadow`}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {createdAdminCredentials && (
        <>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-lg">
              <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="text-lg font-semibold">Admin Created</h3>
              </div>
              <div className="px-4 py-4">
                <p className="text-sm text-gray-600">
                  Share these credentials securely. This password is shown only once.
                </p>
                <div className="mt-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-800">{createdAdminCredentials.email}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Temporary Password</p>
                    <p className="break-all rounded bg-white px-3 py-2 font-mono text-sm text-gray-900">
                      {createdAdminCredentials.password}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end border-t border-gray-200 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setCreatedAdminCredentials(null)}
                  className="rounded bg-cm-gold px-5 py-2 text-sm font-bold uppercase text-cm-gold-ink shadow hover:bg-cm-gold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}


      {/* Force Logout Modal */}
      {forceLogoutModal && selectedUserForLogout && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-3xl'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Force Logout User</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    Are you sure you want to force logout <strong>{selectedUserForLogout.name}</strong>?
                  </p>
                  <p className='text-gray-400 text-xs mt-2'>
                    This will immediately invalidate all their active sessions and they will need to login again.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeForceLogoutModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleForceLogout}
                    disabled={loadingForceLogoutUser}
                  >
                    Force Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Bulk Force Logout Modal */}
      {bulkForceLogoutModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Force Logout Multiple Users</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed mb-3'>
                    Are you sure you want to force logout <strong>{selectedUsersForBulkLogout.length}</strong> selected users?
                  </p>
                  <div className='max-h-32 overflow-y-auto'>
                    {selectedUsersForBulkLogout.map((user, index) => (
                      <p key={index} className='text-xs text-gray-600'>• {user.name} ({user.email || user.phoneNumber})</p>
                    ))}
                  </div>
                  <p className='text-gray-400 text-xs mt-2'>
                    This will immediately invalidate all their active sessions and they will need to login again.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeBulkForceLogoutModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleBulkForceLogout}
                    disabled={loadingForceLogoutMultiple}
                  >
                    Force Logout All
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Delete User Modal */}
      {deleteModal && selectedUserForDelete && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0 text-red-700'>Delete Admin User</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    Are you sure you want to permanently delete <strong>{selectedUserForDelete.name}</strong> ({selectedUserForDelete.email})?
                  </p>
                  <p className='text-red-600 text-xs mt-3 font-semibold'>
                    This cannot be undone. The user will lose access immediately.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-700 text-white active:bg-red-800 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleDeleteAdmin}
                    disabled={loadingDeleteAdmin}
                  >
                    {loadingDeleteAdmin ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Bulk Delete Users Modal */}
      {bulkDeleteModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0 text-red-700'>Delete Multiple Users</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed mb-3'>
                    Are you sure you want to permanently delete <strong>{selectedUsersForBulkLogout.length}</strong> selected users?
                  </p>
                  <div className='max-h-32 overflow-y-auto'>
                    {selectedUsersForBulkLogout.map((user, index) => (
                      <p key={index} className='text-xs text-gray-600'>• {user.name} ({user.email || user.phoneNumber})</p>
                    ))}
                  </div>
                  <p className='text-red-600 text-xs mt-3 font-semibold'>
                    This cannot be undone. Your own account will be skipped if selected.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeBulkDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-700 text-white active:bg-red-800 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleBulkDeleteAdmins}
                    disabled={loadingBulkDeleteAdmins}
                  >
                    {loadingBulkDeleteAdmins ? 'Deleting...' : 'Delete All'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

    </div>
  )
}

export default UserList
